import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { CloudUpload, Loader2 } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

type SyncStatus = {
  status: 'idle' | 'running' | 'completed' | 'error';
  processed: number;
  total: number;
  percent: number;
};

type GoogleConfig = {
  enabledServices: string[];
};

export default function GooglePeopleSyncButton() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [running, setRunning] = useState(false);
  const pollTimer = useRef<number | null>(null);

  const clearPoll = () => {
    if (pollTimer.current !== null) window.clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };

  useEffect(() => clearPoll, []);

  const pollStatus = async () => {
    try {
      const status = await apiRequest('/api/google/people/sync/status') as SyncStatus;
      if (status.status === 'running') {
        setRunning(true);
        pollTimer.current = window.setTimeout(() => void pollStatus(), 1500);
        return;
      }
      setRunning(false);
      clearPoll();
      if (status.status === 'completed') {
        toast({
          title: 'Rubrica sincronizzata',
          description: `${status.processed.toLocaleString('it-IT')} schede controllate.`,
        });
      } else if (status.status === 'error') {
        toast({
          title: 'Sincronizzazione da completare',
          description: 'Apri Impostazioni Google per vedere il dettaglio e riprendere il controllo.',
          variant: 'destructive',
        });
      }
    } catch {
      setRunning(false);
      clearPoll();
    }
  };

  const runSync = async () => {
    if (running) return;
    try {
      const [config, auth] = await Promise.all([
        apiRequest('/api/google/config') as Promise<GoogleConfig>,
        apiRequest('/api/google/auth/status') as Promise<{ connected: boolean }>,
      ]);
      if (!config.enabledServices.includes('people') || !auth.connected) {
        toast({
          title: 'Rubrica Google da configurare',
          description: 'Attiva Rubrica Google e collega l’account nelle Impostazioni.',
        });
        setLocation('/settings/integrations');
        return;
      }
      clearPoll();
      setRunning(true);
      await apiRequest('/api/google/people/sync/run', { method: 'POST', notifySuccess: false });
      toast({
        title: 'Sincronizzazione avviata',
        description: 'Il CRM sta confrontando subito la rubrica con Google Contacts.',
      });
      pollTimer.current = window.setTimeout(() => void pollStatus(), 1000);
    } catch {
      setRunning(false);
      toast({
        title: 'Sincronizzazione non avviata',
        description: 'Controlla la connessione Google nelle Impostazioni del CRM.',
        variant: 'destructive',
      });
    }
  };

  return (
    <button
      type="button"
      onClick={() => void runSync()}
      disabled={running}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-border text-xs hover:bg-accent disabled:cursor-wait disabled:opacity-70 sm:w-auto sm:gap-1.5 sm:px-2.5"
      aria-label={running ? 'Sincronizzazione Google Contacts in corso' : 'Sincronizza Google Contacts adesso'}
      title={running ? 'Sincronizzazione rubrica in corso' : 'Sincronizza subito la rubrica con Google Contacts'}
    >
      {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}
      <span className="hidden lg:inline">Rubrica</span>
    </button>
  );
}
